import React, { useCallback, useEffect, useState } from "react";
import { CompPagination, Container } from "components";
import { AiOutlineCaretDown } from "react-icons/ai";
import { FaEdit, FaEye, FaTrash } from "react-icons/fa";
import { BiSortDown, BiSortUp } from "react-icons/bi";
import { Link, useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { useDispatch, useSelector } from "react-redux";
import { deleteData, getData } from "actions";
import { API_URL_agent, baseurl } from "constants/api";
import { agentReducers } from "redux/agentSlice";
import useDebounce from "hooks/useDebounce";
import axios from "axios";
import { SyncLoader } from "react-spinners";

// field for use sort up/down
const TABLE_HEAD = [
  { title: "Name", field: "first_name" },
  { title: "Email", field: "email" },
  { title: "Phone", field: "phone_number" },
  { title: "Status", field: "is_active" },
  { title: "Last Login", field: "last_login" },
  { title: "Actions", field: "" },
];

const User = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [addParams, setAddParams] = useState("role_group=!admin");

  const {
    getAgentResult,
    getAgentLoading,
    getAgentError,
    addAgentResult,
    deleteAgentResult,
  } = useSelector((state) => state.agent);

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_agent,
        params,
        { dispatch, redux: agentReducers },
        "GET_AGENT"
      );
    },
    [dispatch]
  );

  const onDelete = (item) => {
    deleteData(
      API_URL_agent + item.id,
      { dispatch, redux: agentReducers },
      "DELETE_AGENT"
    );
  };

  const onEdit = (item) => {
    navigate(`/users/userdirectory/edituser`, {
      state: {
        item,
      },
    });
  };

  const onView = (item) => {
    navigate(`/users/userdirectory/view`, {
      state: {
        item,
      },
    });
  };

  const handlePageClick = ({ selected }) => {
    const newOffset = selected * limit;
    const params = `?${addParams}&limit=${limit}&offset=${newOffset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    setOffset(newOffset);
    setCurrentPage(selected);
    get(params);
  };

  const delayedSearch = useCallback(
    useDebounce((value) => {
      //RESET PAGINATE KE 1
      setOffset(0);
      setCurrentPage(0);
      const params = `?${addParams}&limit=${limit}&offset=${offset}&search=${value}&ordering=${
        sortOrder === "desc" ? "-" : ""
      }${sortColumn}`;
      get(params);
    }, 1000),
    [limit, offset, sortOrder, sortColumn]
  );

  const onSearch = (value) => {
    setSearch(value);
    delayedSearch(value);
  };

  const handleSort = (column) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);
    if (column === sortColumn) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // Sort icons
  const renderSortIcon = (field) => {
    if (field === sortColumn) {
      return sortOrder === "asc" ? (
        <BiSortUp className="ml-1 h-4" />
      ) : (
        <BiSortDown className="ml-1 h-4" />
      );
    }
    return <BiSortUp className="ml-1 h-4 text-gray-300" />;
  };

  const handleSelectLimit = (limit) => {
    //RESET PAGINATE KE 1
    setOffset(0);
    setCurrentPage(0);

    const params = `?${addParams}&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
    setLimit(limit);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleExportToExcel = () => {
    axios
      .get(baseurl + "/master/export_excel/?" + addParams, {
        responseType: "arraybuffer",
      }) // Tambahkan responseType: "arraybuffer"
      .then((response) => {
        // Mengubah arraybuffer menjadi Blob
        const blob = new Blob([response.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Membuat URL dari blob untuk diunduh
        const url = window.URL.createObjectURL(blob);

        //get waktu
        const now = moment();

        // Membuat anchor elemen untuk mengunduh file dan klik secara otomatis
        const a = document.createElement("a");
        a.href = url;
        a.download = "Data User " + now.format("DD-MM-YYYY") + ".xlsx"; // Berikan nama file yang benar
        document.body.appendChild(a);
        a.click();

        // Hapus elemen anchor
        document.body.removeChild(a);

        // Hapus URL yang dibuat untuk blob
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const action = [
    {
      name: "Show",
      icon: <FaEye />,
      func: onView,
      colorlight: "bg-green-500",
      colordark: "dark:bg-green-800",
      hoverlight: "hover:bg-green-600",
      hoverdark: "hover:dark:bg-green-900",
    },
    {
      name: "Edit",
      icon: <FaEdit />,
      func: onEdit,
      colorlight: "bg-sky-500",
      colordark: "dark:bg-sky-800",
      hoverlight: "hover:bg-sky-600",
      hoverdark: "hover:dark:bg-sky-900",
    },
    {
      name: "Delete",
      icon: <FaTrash />,
      func: onDelete,
      colorlight: "bg-red-500",
      colordark: "dark:bg-red-800",
      hoverlight: "hover:bg-red-600",
      hoverdark: "hover:dark:bg-red-900",
    },
  ];

  const Options = [
    {
      name: "All Users",
      func: () => {
        setAddParams("role_group=!admin");
        setIsOpen(!isOpen);
        setOffset(0);
        setCurrentPage(0);
      },
    },
    {
      name: "Agent Users",
      func: () => {
        setAddParams("role_group=agent");
        setIsOpen(!isOpen);
        setOffset(0);
        setCurrentPage(0);
      },
    },
    {
      name: "Clients Users",
      func: () => {
        setAddParams("role_group=user");
        setIsOpen(!isOpen);
        setOffset(0);
        setCurrentPage(0);
      },
    },
    {
      name: "Active Users",
      func: () => {
        setAddParams("is_active=True&role_group=!admin");
        setIsOpen(!isOpen);
        setOffset(0);
        setCurrentPage(0);
      },
    },
    {
      name: "Banned Users",
      func: () => {
        setAddParams("ban=1&role_group=!admin");
        setIsOpen(!isOpen);
        setOffset(0);
        setCurrentPage(0);
      },
    },
  ];

  const fetchData = useCallback(() => {
    const params = `?${addParams}&limit=${limit}&offset=${offset}&search=${search}&ordering=${
      sortOrder === "desc" ? "-" : ""
    }${sortColumn}`;
    get(params);
  }, [get, limit, offset, sortColumn, sortOrder, addParams]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [addParams]);

  useEffect(() => {
    if (addAgentResult) {
      fetchData();
    }
  }, [addAgentResult, fetchData]);

  useEffect(() => {
    if (deleteAgentResult) {
      fetchData();
    }
  }, [deleteAgentResult, fetchData]);

  return (
    <Container>
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12  w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 mb-4">
          User
          <div className="col-end-7 flex justify-end">
            <div className="ml-1 relative inline-block flex-col">
              <button
                onClick={toggleDropdown}
                className="flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
              >
                <FaEye />
                View Options
                <AiOutlineCaretDown />
              </button>

              {isOpen && (
                <div className="absolute z-10 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                  <ul
                    className="py-1"
                    role="menu"
                    aria-orientation="vertical"
                    aria-labelledby="options-menu"
                  >
                    {Options.map((item, index) => (
                      <li
                        key={index}
                        className="items-center p-1 list-none hover:bg-gray-100"
                      >
                        <button
                          id={item.name}
                          onClick={() => item.func()}
                          className="px-2 block"
                        >
                          {item.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <button
              onClick={handleExportToExcel}
              className="ml-1 flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
            >
              Export
            </button>
            <Link
              className="ml-1 flex items-center gap-1 p-1 px-3 rounded shadow-sm border bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:border-neutral-600 dark:hover:bg-neutral-800"
              to={"/users/userdirectory/createuser"}
            >
              Create user
            </Link>
          </div>
        </div>

        {/* button perpage and input search  */}
        <div className="flex flex-col md:flex-row md:justify-between align-middle px-4 mb-4 bg-neutral dark:bg-neutral-800 ">
          <div className="p-1 inline-block align-middle">
            <select
              id="limit"
              value={limit}
              onChange={(e) => handleSelectLimit(e.target.value)}
              className="border rounded-sm p-1 border-gray-300 dark:text-black focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
            <span className="mx-2 text-sm ">Records per page</span>
          </div>
          <div className="p-1 inline-block align-middle">
            <span className="text-sm mr-2">Search:</span>
            <input
              id="search"
              type="text"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              placeholder="by name"
              className="p-1 border border-gray-300 dark:text-black focus:border-blue-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
        {/* table and pagination */}
        <div className="mx-4 ">
          <div className="overflow-x-auto min-h-[55vh]">
            <table className="w-full border-collapse border">
              <thead className="text-left">
                <tr className="border">
                  {TABLE_HEAD.map((item, itemIdx) => (
                    <th
                      key={itemIdx}
                      className="p-2 border text-sm whitespace-nowrap cursor-pointer"
                      onClick={() => {
                        item.field && handleSort(item.field);
                      }}
                    >
                      <span className="flex">
                        {item.title}
                        {item.field && renderSortIcon(item.field)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-left">
                {/* loading  */}
                {getAgentLoading && (
                  <tr>
                    <td
                      className="text-center py-12"
                      colSpan={TABLE_HEAD.length + 1}
                    >
                      <div className="pt-10 pb-6 flex justify-center items-center">
                        <SyncLoader color="#4477CE" />
                      </div>
                    </td>
                  </tr>
                )}
                {/* Error */}
                {getAgentError && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-red-500">
                        {getAgentError}
                      </div>
                    </td>
                  </tr>
                )}
                {/* Result = 0 */}
                {getAgentResult.results && getAgentResult.results.length === 0 && (
                  <tr>
                    <td className="text-center" colSpan={TABLE_HEAD.length + 1}>
                      <div className="pt-20 pb-12 flex justify-center items-center text-xs text-slate-600 dark:text-white">
                        No Data
                      </div>
                    </td>
                  </tr>
                )}
                {getAgentResult.results &&
                  getAgentResult.results.map((item, index) => (
                    <tr
                      key={index}
                      className="border bg-gray-50 dark:bg-neutral-900"
                    >
                      <td className="p-2 border">
                        <button
                          className="text-blue-700 dark:text-blue-300"
                          onClick={() => onEdit(item)}
                        >
                          {item.full_name}
                        </button>
                      </td>
                      <td className="p-2 border text-green-700 dark:text-green-200">
                        {item.email}
                      </td>
                      <td className="p-2 border text-black dark:text-white">
                        {item.phone_number}
                      </td>
                      <td className="p-2 border">
                        {item.is_active === false ? (
                          <span className="text-red-500">Inactive</span>
                        ) : (
                          <span className="text-green-700 dark:text-green-200">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="p-2 border text-black dark:text-white">
                        {item.last_login &&
                          moment(item.last_login).format("DD/MM/YYYY HH:mm:ss")}
                      </td>
                      <td className="p-2 border">
                        <div className="flex">
                          {action.map((action, actionIdx) => (
                            <button
                              key={actionIdx}
                              className={`mx-0.5 text-white text-sm ${action.colorlight} ${action.colordark} ${action.hoverlight} ${action.hoverdark} rounded p-1 `}
                              onClick={() => action.func(item)}
                            >
                              <div className="flex items-center">
                                {action.icon} {action.name}
                              </div>
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <div className="pt-5 py-11">
              <CompPagination
                handlePageClick={(e) => handlePageClick(e)}
                pageCount={getAgentResult.count > 0 ? getAgentResult.count : 0}
                limit={limit}
                offset={offset}
                currentPage={currentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default User;
